import React, { useLayoutEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { configureRevenueManager, RevenueManagerConfig } from '@/config';
import Index from '@/pages/Index';

// Create a default query client
const defaultQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

export interface RevenueManagerProps extends RevenueManagerConfig {
  /**
   * Custom QueryClient instance (optional)
   */
  queryClient?: QueryClient;
  
  /**
   * Custom class name for the container
   */
  className?: string;
  
  /**
   * Custom styles for the container
   */
  style?: React.CSSProperties;

  /**
   * Dashboard-selected branch id to preselect
   */
  branchId?: string | number;
}

/**
 * Revenue Manager Component
 * 
 * A complete revenue management dashboard for hospitality properties.
 * 
 * @example
 * ```tsx
 * import { RevenueManager } from '@mettastay/revenue-manager';
 * 
 * function App() {
 *   return (
 *     <RevenueManager
 *       apiBaseUrl="https://api.yourdomain.com/api/v4"
 *       authToken="your-jwt-token"
 *     />
 *   );
 * }
 * ```
 */
export function RevenueManager({
  apiBaseUrl,
  authToken,
  useMock = false,
  queryClient,
  className,
  style,
  branchId,
}: RevenueManagerProps) {
  // Configure the SDK on mount or when config changes
  useLayoutEffect(() => {
    configureRevenueManager({
      apiBaseUrl,
      authToken,
      useMock,
    });
  }, [apiBaseUrl, authToken, useMock]);

  const client = queryClient || defaultQueryClient;

  return (
    <QueryClientProvider client={client}>
      <div className={className} style={style}>
        <Index branchId={branchId} />
        <Toaster />
      </div>
    </QueryClientProvider>
  );
}

export default RevenueManager;
